import React, { useState, useEffect } from "react";
import {
  Button,
  Typography,
  Card,
  CardContent,
  Grid,
  IconButton,
  Box,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import DeleteIcon from "@mui/icons-material/Delete";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import { FieldLayout } from "./FieldLayout";
import { filledFormService } from "@/features/filledForm/filledFormService";

interface InvoiceFieldProps {
  value: any[];
  onChange: (value: any[]) => void;
  label: string;
  description?: string;
  hasErrors?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  videoUrl?: string;
  multiline?: boolean;
  processId: string;
  phaseFormId: string;
  formFieldId?: string;
}

const InvoicePreview = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: '1px solid #e0e0e0',
}));

export interface InvoiceFile {
  id: string;
  fileName: string;
  fileSize: number;
  progress: number;
  status: "uploading" | "completed" | "error";
  path?: string;
  file?: File;
  successful?: number;
  failed?: number;
  errorMessage?: string;
}

export const InvoiceField: React.FC<InvoiceFieldProps> = ({
  value = [],
  onChange,
  label,
  description,
  hasErrors,
  required,
  error,
  helperText,
  videoUrl,
  multiline = false,
  processId,
  phaseFormId,
  formFieldId,
}) => {
  const [invoiceFiles, setInvoiceFiles] = useState<InvoiceFile[]>(value);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  // ID do campo do formulário - usa o fornecido ou extrai do label se não fornecido
  const fieldId = formFieldId || label?.toLowerCase().replace(/\s+/g, '_') || 'invoice_field';

  // Mesmo conserto do UploadField: limpar ao trocar de campo INVOICE.
  useEffect(() => {
    setInvoiceFiles(Array.isArray(value) ? value : []);
  }, [value, formFieldId]);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    // Se não for multiline, limitar a apenas um arquivo
    if (!multiline && (files.length > 1 || invoiceFiles.length > 0)) {
      // Permitir apenas substituir o arquivo existente
      if (invoiceFiles.length > 0) {
        setInvoiceFiles([]);
      }
      // E usar apenas o primeiro arquivo selecionado
      const filesToProcess = [files[0]];
      processFiles(filesToProcess);
    } else {
      processFiles(Array.from(files));
    }
    
    // Limpar o input para permitir selecionar o mesmo arquivo novamente
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  
  const processFiles = async (files: File[]) => {
    // Validar os tipos de arquivo
    const validFiles = files.filter(file => {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (!multiline) {
        // Apenas XML é permitido
        return fileExtension === 'xml';
      } else {
        // XML, ZIP e RAR são permitidos
        return ['xml', 'zip', 'rar'].includes(fileExtension || '');
      }
    });
    
    if (validFiles.length === 0) {
      alert(multiline 
        ? "Apenas arquivos XML, ZIP e RAR são permitidos." 
        : "Apenas arquivos XML são permitidos.");
      return;
    }
    
    // Criar objetos de upload para os arquivos válidos
    const newInvoiceFiles = validFiles.map(file => ({
      id: Math.random().toString(36).substring(2, 11),
      fileName: file.name,
      fileSize: file.size,
      progress: 0,
      status: "uploading" as const,
      file: file
    }));
    
    // Atualizar estado e notificar mudança para o componente pai
    const updatedInvoiceFiles = multiline 
      ? [...invoiceFiles, ...newInvoiceFiles] 
      : newInvoiceFiles;
    
    setInvoiceFiles(updatedInvoiceFiles);
    onChange(updatedInvoiceFiles);
    
    // Iniciar upload de cada arquivo usando o novo serviço
    const uploadPromises = newInvoiceFiles.map(async (invoiceFile) => {
      try {
        // Usar o filledFormService com os parâmetros corretos
        const result = await filledFormService.uploadDocument(
          phaseFormId,
          processId,
          fieldId,
          invoiceFile.file as File,
          (progress) => {
            // Atualizar progresso durante upload
            setInvoiceFiles(prev => {
              const updated = prev.map(f =>
                f.id === invoiceFile.id
                  ? { ...f, progress }
                  : f
              );
              onChange(updated);
              return updated;
            });
          }
        );
        
        // Extrair informações adicionais dos dados da resposta
        let successful: number | undefined;
        let failed: number | undefined;
        
        try {
          // @ts-ignore - Ignorar erros de tipo relacionados à propriedade additionalData
          if (result && result.additionalData) {
            // @ts-ignore - Ignorar erros de tipo
            const additionalDataStr = result.additionalData;
            const additionalData = typeof additionalDataStr === 'string'
              ? JSON.parse(additionalDataStr)
              : additionalDataStr;
              
            successful = additionalData?.successful;
            failed = additionalData?.failed;
          }
        } catch (e) {
          console.error("Erro ao analisar additionalData:", e);
        }
        
        // Atualizar status quando upload concluído
        setInvoiceFiles(prev => {
          const updated = prev.map(f =>
            f.id === invoiceFile.id
              ? { 
                  ...f, 
                  status: "completed" as const, 
                  path: result.path, 
                  progress: 100,
                  successful,
                  failed
                }
              : f
          );
          onChange(updated);
          return updated;
        });
        
        return result.path;
      } catch (error) {
        // Atualizar status em caso de erro
        let errorMessage = "Erro ao fazer upload do arquivo";
        let failed = undefined;
        
        // Tentar extrair informações de erro detalhadas quando disponíveis
        try {
          if (error && typeof error === 'object') {
            // @ts-ignore
            const errorData = error.response?.data || error;
            
            // Verificar se é um erro de validação com detalhes sobre notas fiscais
            if (errorData.code === 'ValidationError' && errorData.validationErrors?.invoice) {
              const invoiceError = errorData.validationErrors.invoice;
              errorMessage = invoiceError.message || "Erro na validação das notas fiscais";
              
              if (invoiceError.value && typeof invoiceError.value === 'object') {
                failed = invoiceError.value.failed;
              }
            } else if (errorData.message) {
              errorMessage = errorData.message;
            }
          }
        } catch (e) {
          console.error("Erro ao processar detalhes do erro:", e);
        }
        
        setInvoiceFiles(prev => {
          const updated = prev.map(f =>
            f.id === invoiceFile.id
              ? { 
                  ...f, 
                  status: "error" as const,
                  errorMessage,
                  failed
                }
              : f
          );
          onChange(updated);
          return updated;
        });
        
        console.error("Erro ao fazer upload do arquivo:", error);
        return null;
      }
    });
    
    await Promise.all(uploadPromises);
  };
  
  const handleRemoveFile = (id: string) => {
    const updatedFiles = invoiceFiles.filter(file => file.id !== id);
    setInvoiceFiles(updatedFiles);
    onChange(updatedFiles);
  };
  
  // Determinar os tipos de arquivo aceitos
  const acceptFileTypes = multiline ? ".xml,.zip,.rar" : ".xml";

  return (
    <FieldLayout
      label={label}
      description={description}
      error={error}
      helperText={helperText}
      videoUrl={videoUrl}
    >
      <Box>
        <Typography variant="subtitle1" gutterBottom>
          {multiline 
            ? "Adicione as notas fiscais relacionadas a este processo (XML, ZIP ou RAR)" 
            : "Adicione uma nota fiscal XML relacionada a este processo"}
        </Typography>
        
        {/* Input de arquivo oculto */}
        <input
          type="file"
          accept={acceptFileTypes}
          multiple={multiline}
          onChange={handleFileSelect}
          style={{ display: "none" }}
          ref={fileInputRef}
          id={`invoice-file-input-${fieldId}`}
        />
        
        <label htmlFor={`invoice-file-input-${fieldId}`}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<UploadFileIcon />}
            disabled={!multiline && invoiceFiles.length > 0}
            sx={{ mt: 2, mb: 2 }}
            component="span"
          >
            {multiline ? "Adicionar Arquivos" : "Selecionar Arquivo XML"}
          </Button>
        </label>
        
        {/* Exibir mensagem informativa sobre os tipos de arquivo */}
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1, mb: 2 }}>
          {multiline 
            ? "Formatos aceitos: XML, ZIP, RAR. Você pode adicionar múltiplos arquivos." 
            : "Formato aceito: XML. Você pode adicionar apenas um arquivo."}
        </Typography>
        
        {/* Exibir arquivos em upload ou já carregados */}
        {invoiceFiles.length > 0 ? (
          invoiceFiles.map((invoiceFile) => (
            <InvoicePreview key={invoiceFile.id}>
              <CardContent>
                <Grid container spacing={2} alignItems="center">
                  <Grid item>
                    <DescriptionIcon fontSize="large" color="primary" />
                  </Grid>
                  <Grid item xs>
                    <Typography variant="subtitle1">{invoiceFile.fileName}</Typography>
                    <Typography variant="body2">
                      Tamanho: {(invoiceFile.fileSize / 1024).toFixed(2)} KB
                    </Typography>
                    
                    {/* Status do upload */}
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Chip 
                        label={
                          invoiceFile.status === 'uploading' ? `Enviando (${Math.round(invoiceFile.progress)}%)` :
                          invoiceFile.status === 'completed' ? 'Enviado' : 'Erro'
                        }
                        color={
                          invoiceFile.status === 'uploading' ? 'primary' :
                          invoiceFile.status === 'completed' ? 'success' : 'error'
                        }
                        size="small"
                        sx={{ mr: 1 }}
                      />
                    </Box>
                    
                    {/* Mostrar informações adicionais sobre as notas processadas quando sucesso */}
                    {invoiceFile.status === 'completed' && (
                      <Box sx={{ mt: 1 }}>
                        {invoiceFile.successful !== undefined && (
                          <Typography variant="body2" color="text.secondary">
                            <strong>{invoiceFile.successful}</strong> notas importadas com sucesso
                            {invoiceFile.failed !== undefined && invoiceFile.failed > 0 && 
                             `, <strong>${invoiceFile.failed}</strong> notas com erro`}.
                          </Typography>
                        )}
                      </Box>
                    )}
                    
                    {/* Mostrar informações de erro quando falha */}
                    {invoiceFile.status === 'error' && (
                      <Box sx={{ mt: 1 }}>
                        <Typography variant="body2" color="error">
                          {invoiceFile.errorMessage || "Erro ao processar o arquivo"}
                          {invoiceFile.failed !== undefined && (
                            <span> (<strong>{invoiceFile.failed}</strong> notas com erro)</span>
                          )}
                        </Typography>
                      </Box>
                    )}
                    
                    {/* Barra de progresso se estiver enviando */}
                    {invoiceFile.status === 'uploading' && (
                      <Box sx={{ width: '100%', mt: 1 }}>
                        <div style={{ 
                          height: '4px', 
                          width: `${invoiceFile.progress}%`,
                          backgroundColor: '#2196f3',
                          borderRadius: '2px'
                        }} />
                      </Box>
                    )}
                  </Grid>
                  <Grid item>
                    <IconButton 
                      aria-label="delete" 
                      onClick={() => handleRemoveFile(invoiceFile.id)}
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Grid>
                </Grid>
              </CardContent>
            </InvoicePreview>
          ))
        ) : (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
            Nenhum arquivo de nota fiscal adicionado ainda
          </Typography>
        )}
      </Box>
    </FieldLayout>
  );
}; 